import { Dayjs } from "dayjs";

/**
 * HO dashboard global period. Mirrors the backend `dashboard_period_enum`.
 * - today      → a single day
 * - this_month → the whole month containing the anchor date
 * - this_year  → the whole year containing the anchor date
 */
export type DashboardPeriod = "today" | "this_month" | "this_year";

export interface PeriodRange {
  date_from: string;
  date_to: string;
  /** Drives waste/leftover bucketing (mirrors getWasteBreakdown). */
  period_type: "day" | "month" | "year";
}

const DATE_FORMAT = "YYYY-MM-DD";

/**
 * Map a user-picked period + anchor date to YYYY-MM-DD ranges plus the
 * period_type used by the waste/leftover endpoints. Backend `getSummary`
 * derives the previous comparison window from this range automatically.
 */
export function getPeriodRange(
  period: DashboardPeriod,
  anchor: Dayjs,
): PeriodRange {
  switch (period) {
    case "this_month":
      return {
        date_from: anchor.startOf("month").format(DATE_FORMAT),
        date_to: anchor.endOf("month").format(DATE_FORMAT),
        period_type: "day",
      };
    case "this_year":
      return {
        date_from: anchor.startOf("year").format(DATE_FORMAT),
        date_to: anchor.endOf("year").format(DATE_FORMAT),
        period_type: "year",
      };
    case "today":
    default:
      return {
        date_from: anchor.format(DATE_FORMAT),
        date_to: anchor.format(DATE_FORMAT),
        period_type: "day",
      };
  }
}

/** Thai label for the period control (รายวัน / รายเดือน / รายปี). */
export const PERIOD_LABEL: Record<DashboardPeriod, string> = {
  today: "รายวัน",
  this_month: "รายเดือน",
  this_year: "รายปี",
};

/** Thai "vs previous period" label used on change badges. */
export const PERIOD_PREVIOUS_LABEL: Record<DashboardPeriod, string> = {
  today: "จากเมื่อวาน",
  this_month: "จากเดือนก่อน",
  this_year: "จากปีก่อน",
};

/** Thai noun for the current period (used in card titles, e.g. ยอดขายสุทธิวันนี้). */
export const PERIOD_NOUN_LABEL: Record<DashboardPeriod, string> = {
  today: "วันนี้",
  this_month: "เดือนนี้",
  this_year: "ปีนี้",
};
